
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SUBMIT_URL = 'https://readdy.ai/api/form/d6j4rijcs59vsrrrjsq0';
const VOTED_KEY = 'meow_challenge_voted';

const CHALLENGES = [
  {
    id: 'c1',
    day: 'Today\'s Challenge',
    emoji: '📸',
    title: 'Catch Your Cat Mid-Zoomies',
    description: 'We know your cat has the zoomies at 3am. Catch them in action and share for a chance to win $50 in cat products!',
    prize: '$50 Cat Product Bundle',
    entries: 1847,
    timeLeft: '14h 32m',
    color: 'from-amber-400 to-orange-500',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    options: [
      { id: 'morning', label: 'Morning Zoomies 🌅', votes: 412 },
      { id: 'night', label: 'Midnight Madness 🌙', votes: 689 },
      { id: 'random', label: 'Random Chaos 🌀', votes: 521 },
      { id: 'after_poop', label: 'Post-Litter Sprint 💨', votes: 225 },
    ],
  },
];

const WINNERS = [
  { name: 'Melissa R.', location: 'Denver, CO', cat: 'Biscuit', prize: '$50 Bundle', week: 'Last Week', emoji: '🏆' },
  { name: 'Tom K.', location: 'Seattle, WA', cat: 'Noodle', prize: 'Premium Toy Set', week: '2 Weeks Ago', emoji: '🥈' },
  { name: 'Aisha P.', location: 'Miami, FL', cat: 'Cleo', prize: 'Gourmet Treats Box', week: '3 Weeks Ago', emoji: '🥉' },
];

export default function DailyCatChallenge() {
  const challenge = CHALLENGES[0];
  const [voted, setVoted] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [votes, setVotes] = useState<Record<string, number>>(
    Object.fromEntries(challenge.options.map(o => [o.id, o.votes]))
  );
  const [email, setEmail] = useState('');
  const [entryName, setEntryName] = useState('');
  const [entryStatus, setEntryStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ h: 14, m: 32, s: 0 });

  useEffect(() => {
    const savedVote = localStorage.getItem(VOTED_KEY);
    if (savedVote) {
      setVoted(true);
      setSelectedOption(savedVote);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        let { h, m, s } = prev;
        s -= 1;
        if (s < 0) { s = 59; m -= 1; }
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 23; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleVote = (optionId: string) => {
    if (voted) return;
    setVotes(prev => ({ ...prev, [optionId]: prev[optionId] + 1 }));
    setSelectedOption(optionId);
    setVoted(true);
    localStorage.setItem(VOTED_KEY, optionId);
    setTimeout(() => setShowEntryForm(true), 600);
  };

  const totalVotes = Object.values(votes).reduce((a, b) => a + b, 0);

  const handleEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !entryName) return;
    setEntryStatus('submitting');
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      params.append('name', entryName);
      params.append('challenge', challenge.title);
      params.append('vote', selectedOption || '');
      params.append('source', 'daily_challenge');
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) setEntryStatus('success');
      else setEntryStatus('error');
    } catch {
      setEntryStatus('error');
    }
  };

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 text-sm font-bold rounded-full mb-4">
            <i className="ri-trophy-line"></i>
            Daily Challenge
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Win Free Cat Products Every Day 🏆
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Vote, enter your email, and win. A new challenge drops every 24 hours.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Main Challenge Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-amber-100">
              {/* Top gradient bar */}
              <div className={`h-2 w-full bg-gradient-to-r ${challenge.color}`} />

              <div className="p-6 md:p-8">
                {/* Timer + Prize */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                      <i className="ri-time-line text-sm"></i>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-medium">Ends in</div>
                      <div className="font-bold text-gray-900 text-sm tabular-nums">
                        {pad(timeLeft.h)}:{pad(timeLeft.m)}:{pad(timeLeft.s)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
                    <i className="ri-gift-2-line text-amber-500 text-sm"></i>
                    <span className="text-xs font-bold text-amber-700">Prize: {challenge.prize}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    <strong className="text-gray-700">{(challenge.entries + totalVotes - Object.values(challenge.options).reduce((a, b) => a + b.votes, 0)).toLocaleString()}</strong> entries today
                  </div>
                </div>

                {/* Challenge Title */}
                <div className="flex items-start gap-3 mb-5">
                  <span className="text-3xl">{challenge.emoji}</span>
                  <div>
                    <div className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-1">{challenge.day}</div>
                    <h3 className="text-xl font-bold text-gray-900">{challenge.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{challenge.description}</p>
                  </div>
                </div>

                {/* Vote Options */}
                <div className="space-y-2.5 mb-5">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">When does your cat get the zoomies?</p>
                  {challenge.options.map(option => {
                    const pct = totalVotes > 0 ? Math.round((votes[option.id] / totalVotes) * 100) : 0;
                    const isSelected = selectedOption === option.id;
                    const isWinner = voted && votes[option.id] === Math.max(...Object.values(votes));
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleVote(option.id)}
                        disabled={voted}
                        className={`w-full relative overflow-hidden rounded-xl border-2 text-left transition-all duration-300 ${
                          voted ? 'cursor-default' : 'cursor-pointer hover:border-amber-400'
                        } ${isSelected ? 'border-amber-500' : isWinner && voted ? 'border-amber-300' : 'border-gray-200'}`}
                      >
                        {voted && (
                          <div
                            className={`absolute inset-0 transition-all duration-700 ${isWinner ? 'bg-amber-100' : 'bg-gray-50'}`}
                            style={{ width: `${pct}%` }}
                          />
                        )}
                        <div className="relative z-10 flex items-center justify-between px-4 py-2.5">
                          <span className="text-sm font-semibold text-gray-800">{option.label}</span>
                          {voted && (
                            <span className={`text-sm font-bold ${isWinner ? 'text-amber-600' : 'text-gray-400'}`}>{pct}%</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Entry Form — appears after voting */}
                <AnimatePresence>
                  {showEntryForm && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      {entryStatus === 'success' ? (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
                          <div className="text-2xl mb-1">🎉</div>
                          <p className="text-sm font-bold text-teal-700">You're entered to win!</p>
                          <p className="text-xs text-teal-600 mt-1">We'll email you if you win. Good luck!</p>
                        </div>
                      ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <p className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2">
                            <i className="ri-trophy-fill text-amber-500"></i>
                            Enter your email to win the prize!
                          </p>
                          <form data-readdy-form onSubmit={handleEntry} className="flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              name="name"
                              value={entryName}
                              onChange={e => setEntryName(e.target.value)}
                              required
                              placeholder="Your name"
                              className="flex-1 border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 bg-white"
                            />
                            <input
                              type="email"
                              name="email"
                              value={email}
                              onChange={e => setEmail(e.target.value)}
                              required
                              placeholder="Your email"
                              className="flex-1 border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 bg-white"
                            />
                            <button
                              type="submit"
                              disabled={entryStatus === 'submitting'}
                              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap"
                            >
                              {entryStatus === 'submitting' ? <i className="ri-loader-4-line animate-spin"></i> : 'Enter to Win!'}
                            </button>
                          </form>
                          {entryStatus === 'error' && <p className="text-rose-500 text-xs mt-1">Something went wrong. Try again.</p>}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {!voted && (
                  <p className="text-xs text-gray-400 text-center mt-3">
                    Vote to unlock the entry form and win today's prize 🎁
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Winners Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col gap-4"
          >
            <div className="bg-white rounded-2xl shadow-md border border-amber-100 p-5">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-medal-line text-amber-500"></i>
                Recent Winners
              </h4>
              <div className="space-y-4">
                {WINNERS.map((w, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-50 text-lg flex-shrink-0">
                      {w.emoji}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{w.name}</p>
                      <p className="text-xs text-gray-400">{w.location} · Cat: <span className="text-teal-600 font-semibold">{w.cat}</span></p>
                      <p className="text-xs text-amber-600 font-semibold mt-0.5">Won: {w.prize}</p>
                      <p className="text-xs text-gray-300">{w.week}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-5 text-white">
              <div className="text-2xl mb-2">🐾</div>
              <h4 className="font-bold text-base mb-1">Share & Double Your Chances</h4>
              <p className="text-xs text-teal-100 mb-3">Share today's challenge with a friend and both of you get an extra entry!</p>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: 'Daily Cat Challenge!', url: window.location.href });
                  }
                }}
                className="w-full py-2 bg-white text-teal-600 font-bold rounded-lg text-sm hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-line mr-1"></i> Share Challenge
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
